import React, { useState, useMemo, useEffect } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowLeft, Search, BookOpen, ChevronRight, Sparkles } from 'lucide-react';
import { blogPosts, categories } from '../data/blogPosts';

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
}

export function BlogListPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const filteredPosts = useMemo(() => {
    return blogPosts.filter(post => {
      const matchesSearch = post.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           post.answer.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = selectedCategory === 'All' || post.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [searchTerm, selectedCategory]);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="relative z-20 px-6 md:px-12 py-6 bg-white/95 backdrop-blur-md border-b border-charcoal/5 sticky top-0">
        <div className="max-w-[1400px] mx-auto flex justify-between items-center">
          <Link to="/" className="flex items-center gap-4">
            <div className="flex flex-col">
              <div className="font-heading text-2xl font-bold tracking-tight text-charcoal leading-none">
                Essex Photo & Video
              </div>
              <div className="text-xs tracking-[0.3em] uppercase text-gold font-semibold mt-1">
                Finder
              </div>
            </div>
          </Link>
          <button
            onClick={() => navigate('/quiz')}
            data-testid="blog-quiz-btn"
            className="flex items-center gap-2 px-6 py-2.5 rounded-full bg-gradient-to-r from-gold to-gold-dim text-white text-sm font-bold uppercase tracking-wide hover:shadow-xl transition-all duration-300"
          >
            Take Free Quiz
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="px-6 md:px-12 py-16 bg-gradient-to-b from-pearl to-white">
        <div className="max-w-[1400px] mx-auto text-center">
          <div className="mb-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold/10 border border-gold/20">
            <BookOpen className="w-4 h-4 text-gold" />
            <span className="text-xs uppercase tracking-wider text-gold font-bold">Wedding Photography Guide</span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold tracking-tight mb-6 text-charcoal">
            Your Questions, Answered
          </h1>
          <p className="text-lg text-charcoal/60 max-w-2xl mx-auto mb-10">
            Everything you need to know about wedding photography and videography in Essex. 
            Expert answers to help you plan your perfect day.
          </p>

          {/* Search */}
          <div className="relative max-w-xl mx-auto mb-8">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-charcoal/40" />
            <input
              type="text"
              placeholder="Search questions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-4 rounded-full border-2 border-charcoal/10 focus:border-gold focus:outline-none transition-colors duration-300 text-charcoal"
            />
          </div>

          {/* Categories */}
          <div className="flex flex-wrap justify-center gap-2">
            <button
              onClick={() => setSelectedCategory('All')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                selectedCategory === 'All' 
                  ? 'bg-charcoal text-white' 
                  : 'bg-charcoal/5 text-charcoal/70 hover:bg-charcoal/10'
              }`}
            >
              All ({blogPosts.length})
            </button>
            {categories.map(category => {
              const count = blogPosts.filter(p => p.category === category).length;
              return (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                    selectedCategory === category 
                      ? 'bg-charcoal text-white' 
                      : 'bg-charcoal/5 text-charcoal/70 hover:bg-charcoal/10'
                  }`}
                >
                  {category} ({count})
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="px-6 md:px-12 py-16">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPosts.map((post, index) => (
              <motion.article
                key={post.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
              >
                <Link
                  to={`/blog/${post.slug}`}
                  className="block h-full p-6 bg-white border-2 border-charcoal/10 rounded-2xl hover:border-gold hover:shadow-xl transition-all duration-300 group"
                >
                  <span className="inline-block px-3 py-1 bg-gold/10 text-gold text-xs font-bold uppercase tracking-wide rounded-full mb-4">
                    {post.category}
                  </span>
                  <h2 className="text-xl font-heading font-semibold text-charcoal mb-3 group-hover:text-gold transition-colors duration-300 line-clamp-2">
                    {post.question}
                  </h2>
                  <p className="text-charcoal/60 text-sm line-clamp-3 mb-4">
                    {post.answer.substring(0, 150)}...
                  </p>
                  <span className="inline-flex items-center gap-2 text-gold text-sm font-semibold">
                    Read Answer
                    <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                  </span>
                </Link>
              </motion.article>
            ))}
          </div>

          {filteredPosts.length === 0 && (
            <div className="text-center py-16">
              <p className="text-charcoal/60 text-lg">No articles found matching your search.</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 md:px-12 py-20 bg-gradient-to-br from-charcoal to-charcoal/95">
        <div className="max-w-[800px] mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4">
            Ready to Find Your Perfect Match?
          </h2>
          <p className="text-white/70 mb-8">
            Take our free 30-second quiz and get matched with Essex's top wedding photographers and videographers.
          </p>
          <button
            onClick={() => navigate('/quiz')}
            className="inline-flex items-center gap-3 px-10 py-5 rounded-full bg-gradient-to-r from-gold to-gold-dim text-white text-base font-bold uppercase tracking-wide hover:shadow-2xl hover:shadow-gold/30 transition-all duration-500 hover:scale-105"
          >
            <Sparkles className="w-5 h-5" />
            Start Free Quiz
            <ArrowRight className="w-5 h-5" />
          </button>
          <p className="mt-4 text-white/50 text-sm">
            No sign up required
          </p>
        </div>
      </section>
    </div>
  );
}

export function BlogPostPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  
  const post = blogPosts.find(p => p.slug === slug);
  const currentIndex = blogPosts.findIndex(p => p.slug === slug);
  const prevPost = currentIndex > 0 ? blogPosts[currentIndex - 1] : null;
  const nextPost = currentIndex < blogPosts.length - 1 ? blogPosts[currentIndex + 1] : null;
  
  // Related posts from same category
  const relatedPosts = blogPosts
    .filter(p => p.category === post?.category && p.id !== post?.id)
    .slice(0, 3);

  if (!post) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-heading font-bold text-charcoal mb-4">Article not found</h1>
          <Link to="/blog" className="text-gold hover:underline">Back to all articles</Link>
        </div>
      </div>
    );
  }

  // Convert markdown-style formatting to HTML
  const formatContent = (content) => {
    return content
      .split('\n\n')
      .map((paragraph, i) => {
        // Handle headers
        if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
          return <h3 key={i} className="text-xl font-heading font-semibold text-charcoal mt-8 mb-4">{paragraph.replace(/\*\*/g, '')}</h3>;
        }
        
        // Handle list items
        if (paragraph.includes('\n-') || paragraph.startsWith('-')) {
          const lines = paragraph.split('\n');
          return (
            <ul key={i} className="list-disc list-inside space-y-2 my-4 text-charcoal/80">
              {lines.map((line, j) => {
                if (line.startsWith('-')) {
                  return <li key={j}>{line.substring(1).trim().replace(/\*\*/g, '')}</li>;
                }
                if (line.startsWith('✅') || line.startsWith('❌') || line.startsWith('✓') || line.startsWith('📱') || line.startsWith('📉')) {
                  return <li key={j} className="list-none">{line}</li>;
                }
                return line ? <p key={j} className="font-semibold text-charcoal mt-4 mb-2">{line.replace(/\*\*/g, '')}</p> : null;
              })}
            </ul>
          );
        }

        // Handle bold text within paragraphs
        const formattedText = paragraph.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        
        return (
          <p 
            key={i} 
            className="text-charcoal/80 leading-relaxed my-4"
            dangerouslySetInnerHTML={{ __html: formattedText }}
          />
        );
      });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="relative z-20 px-6 md:px-12 py-6 bg-white/95 backdrop-blur-md border-b border-charcoal/5 sticky top-0">
        <div className="max-w-[1400px] mx-auto flex justify-between items-center">
          <Link to="/" className="flex items-center gap-4">
            <div className="flex flex-col">
              <div className="font-heading text-2xl font-bold tracking-tight text-charcoal leading-none">
                Essex Photo & Video
              </div>
              <div className="text-xs tracking-[0.3em] uppercase text-gold font-semibold mt-1">
                Finder
              </div>
            </div>
          </Link>
          <button
            onClick={() => navigate('/quiz')}
            className="flex items-center gap-2 px-6 py-2.5 rounded-full bg-gradient-to-r from-gold to-gold-dim text-white text-sm font-bold uppercase tracking-wide hover:shadow-xl transition-all duration-300"
          >
            Take Free Quiz
          </button>
        </div>
      </header>

      {/* Breadcrumb */}
      <div className="px-6 md:px-12 py-4 bg-pearl/50">
        <div className="max-w-[900px] mx-auto flex items-center gap-2 text-sm text-charcoal/60">
          <Link to="/" className="hover:text-gold transition-colors">Home</Link>
          <ChevronRight className="w-4 h-4" />
          <Link to="/blog" className="hover:text-gold transition-colors">Blog</Link>
          <ChevronRight className="w-4 h-4" />
          <span className="text-charcoal/40 truncate">{post.question}</span>
        </div>
      </div>

      {/* Article */}
      <article className="px-6 md:px-12 py-12">
        <div className="max-w-[900px] mx-auto">
          <span className="inline-block px-3 py-1 bg-gold/10 text-gold text-xs font-bold uppercase tracking-wide rounded-full mb-6">
            {post.category}
          </span>
          
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold tracking-tight text-charcoal mb-8 leading-tight">
            {post.question}
          </h1>

          <div className="prose prose-lg max-w-none">
            {formatContent(post.answer)}
          </div>

          {/* Quiz CTA */}
          <div className="mt-12 p-8 bg-gradient-to-br from-gold/10 to-gold/5 rounded-3xl border border-gold/20">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="flex-1">
                <h3 className="text-2xl font-heading font-bold text-charcoal mb-2">
                  Find Your Perfect {post.question.toLowerCase().includes('video') || post.category === 'Videography' ? 'Videographer' : 'Photographer'}
                </h3>
                <p className="text-charcoal/60">
                  Take our free 30-second quiz to get matched with Essex's top-rated wedding {post.question.toLowerCase().includes('video') || post.category === 'Videography' ? 'videographers' : 'photographers and videographers'}.
                </p>
              </div>
              <button
                onClick={() => navigate('/quiz')}
                className="flex items-center gap-3 px-8 py-4 rounded-full bg-gradient-to-r from-gold to-gold-dim text-white font-bold uppercase tracking-wide hover:shadow-xl transition-all duration-300 whitespace-nowrap"
              >
                <Sparkles className="w-5 h-5" />
                Take Free Quiz
              </button>
            </div>
          </div>

          {/* Navigation */}
          <div className="mt-12 flex justify-between items-center border-t border-charcoal/10 pt-8">
            {prevPost ? (
              <Link 
                to={`/blog/${prevPost.slug}`}
                className="flex items-center gap-2 text-charcoal/60 hover:text-gold transition-colors group"
              >
                <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
                <span className="text-sm">Previous</span>
              </Link>
            ) : <div />}
            
            <Link 
              to="/blog"
              className="text-sm text-charcoal/60 hover:text-gold transition-colors"
            >
              All Articles
            </Link>
            
            {nextPost ? (
              <Link 
                to={`/blog/${nextPost.slug}`}
                className="flex items-center gap-2 text-charcoal/60 hover:text-gold transition-colors group"
              >
                <span className="text-sm">Next</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            ) : <div />}
          </div>
        </div>
      </article>

      {/* Related Articles */}
      {relatedPosts.length > 0 && (
        <section className="px-6 md:px-12 py-16 bg-pearl/30">
          <div className="max-w-[1400px] mx-auto">
            <h2 className="text-2xl font-heading font-bold text-charcoal mb-8">Related Articles</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedPosts.map(related => (
                <Link
                  key={related.id}
                  to={`/blog/${related.slug}`}
                  className="block p-6 bg-white border-2 border-charcoal/10 rounded-2xl hover:border-gold hover:shadow-xl transition-all duration-300 group"
                >
                  <h3 className="text-lg font-heading font-semibold text-charcoal mb-2 group-hover:text-gold transition-colors line-clamp-2">
                    {related.question}
                  </h3>
                  <span className="inline-flex items-center gap-1 text-gold text-sm font-semibold">
                    Read More
                    <ChevronRight className="w-4 h-4" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
